import { useMemo } from 'react';
import { useToastContext } from '../contexts/ToastContext';

/**
 * API pública dos toasts da aplicação.
 *
 * Mesma assinatura do `useToast` do LeafyGreen (`success/error/warning/info/
 * loading/dismiss`) para não tocar os ~60 arquivos que já usam — só a
 * implementação mudou: o LeafyGreen usava `pushToast`/`popToast`, agora a
 * lista vive no `ToastContext` e é renderizada pelo `Flashbar` do Cloudscape.
 *
 * O objeto devolvido é estável: ele é usado em deps de useEffect/useCallback
 * (ex.: DashboardPage). Um objeto novo a cada render faria o efeito rodar de
 * novo a cada toast exibido → loop infinito de requisições em caso de erro.
 */
export function useAppToast() {
  const { push, dismiss } = useToastContext();

  return useMemo(() => ({
    success: (title: string, description?: string) =>
      push({ title, description, type: 'success', timeout: 5000 }),

    // P3 review 2026-09-24: erro usava variant 'warning' (amarelo) — confuso
    // porque 'warning' é amarelo. O tipo 'error' do Cloudscape é vermelho
    // (chama atenção do usuário) e 'warning' continua sendo o aviso amarelo.
    error: (title: string, description?: string) =>
      push({ title, description, type: 'error', timeout: 6000 }),

    warning: (title: string, description?: string) =>
      push({ title, description, type: 'warning', timeout: 6000 }),

    info: (title: string, description?: string) =>
      push({ title, description, type: 'info', timeout: 5000 }),

    loading: (title: string) =>
      push({ title, type: 'in-progress', loading: true, timeout: null }),

    dismiss: (id: string) => dismiss(id),
  }), [push, dismiss]);
}
